import { MediaElement, MediaImage, MediaLine, MediaRectangle, MediaText } from 'editor/src/store/design/types';
import { MockupRole } from 'editor/src/store/mockup/types';

import { getRectangleBoundingBox } from 'editor/src/util/reflectDesignData/utils';

/**
 * Elements that can belong to a reflow row. Text is the original (and only
 * editable) member type; line/rectangle/image can join purely as decoration
 * (e.g. a rule above a personalizable text, or a divider between two texts in
 * a horizontal row) — see computeReflowState.ts for how their presence is
 * treated differently from text (they never keep a row alive on their own).
 * Mockup placeholders, addons and grids are deliberately excluded: a
 * placeholder is a rendering slot rather than authored content, and addons/
 * grids have no established use case here.
 */
export type ReflowMember = MediaText | MediaLine | MediaRectangle | MediaImage;

export function isReflowMember(element: MediaElement): element is ReflowMember {
  if (element.type === 'text' || element.type === 'line' || element.type === 'rectangle') {
    return true;
  }
  if (element.type === 'image') {
    return element.role !== MockupRole.Placeholder;
  }
  return false;
}

export interface ReflowBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Axis-aligned bounding box (mm) of a reflow member, in the row's coordinate
 * space.
 *
 * A line's endpoints already encode its own rotation, so its bbox is a direct
 * min/max of (x1,y1)-(x2,y2) padded by half the stroke width — the visible
 * stroke extends that far past the mathematical line.
 *
 * Rectangles and images rotate around their top-left corner (x, y), same
 * convention as getElementBoundingBox / the align operations, so we reuse
 * getRectangleBoundingBox instead of re-deriving rotated corners here.
 */
export function getReflowBounds(element: ReflowMember): ReflowBounds {
  if (element.type === 'line') {
    const halfStroke = element.strokeWidth / 2;
    return {
      top: Math.min(element.y1, element.y2) - halfStroke,
      bottom: Math.max(element.y1, element.y2) + halfStroke,
      left: Math.min(element.x1, element.x2) - halfStroke,
      right: Math.max(element.x1, element.x2) + halfStroke,
    };
  }
  const box = getRectangleBoundingBox(element.x, element.y, element.width, element.height, element.r);
  return { top: box.y, bottom: box.y + box.height, left: box.x, right: box.x + box.width };
}

/**
 * Top/bottom used to compute a row's authored slot. Kept text-exact (plain
 * `y`/`y + height`, ignoring rotation) so existing text-only rows compute
 * byte-identical shifts to before this change — text has never rotated inside
 * a reflow row and we don't want to introduce a behaviour change there. Non-
 * text members use the full rotated/stroke-aware bounding box.
 */
export function getReflowMemberVerticalBounds(element: ReflowMember): { top: number; bottom: number } {
  if (element.type === 'text') {
    return { top: element.y, bottom: element.y + element.height };
  }
  const bounds = getReflowBounds(element);
  return { top: bounds.top, bottom: bounds.bottom };
}

/** Left edge used for horizontal packing order/anchoring. Text keeps using its
 *  own `x` (unchanged behaviour); non-text members use their bounding box's
 *  left edge. */
export function getReflowMemberLeft(element: ReflowMember): number {
  return element.type === 'text' ? element.x : getReflowBounds(element).left;
}

export type ReflowMemberUpdate = { x?: number; y?: number } | { x1?: number; y1?: number; x2?: number; y2?: number };

/**
 * Returns the partial update to move a member by (dx, dy) mm. Text/rectangle/
 * image move their (x, y) origin; a line moves both endpoints so its length
 * and angle are preserved. Only the axes that actually change are included.
 */
export function moveReflowMember(element: ReflowMember, dx: number, dy: number): ReflowMemberUpdate {
  if (element.type === 'line') {
    const update: { x1?: number; y1?: number; x2?: number; y2?: number } = {};
    if (dx !== 0) {
      update.x1 = element.x1 + dx;
      update.x2 = element.x2 + dx;
    }
    if (dy !== 0) {
      update.y1 = element.y1 + dy;
      update.y2 = element.y2 + dy;
    }
    return update;
  }
  const update: { x?: number; y?: number } = {};
  if (dx !== 0) {
    update.x = element.x + dx;
  }
  if (dy !== 0) {
    update.y = element.y + dy;
  }
  return update;
}
